import { useTranslation } from "react-i18next";

import { IsAllowed } from "../../acl";
import { resources } from "../../acl/resources";
import { useDeleteBuildingProfileFile } from "../../api/hooks/allHooks";
import { eventNames, trackGAEvent } from "../../functions/googleAnalytics";
import { convertDatetimeStringToDate } from "../../utils/date";
import { IconButton } from "../IconButton";
import { LoadingOrEmptyWrapper } from "../LoadingAndEmptyHandler";
import { Table } from "../Table";
import {
  BuildingProfileFileSection,
  BuildingProfileFileSectionName,
  buildingProfileFilesSections,
} from "./BuildingProfileTableTabs";
import { TableColumn } from "./types";

type BuildingProfileFile = {
  _updated_at: string;
  file_url: string;
  id: string;
  name: string;
  section: BuildingProfileFileSectionName;
  type: string;
};

type BuildingProfileTableProps = {
  buildingFiles: BuildingProfileFile[];
  isLoading: boolean;
  projectId: number;
  selectedTab: BuildingProfileFileSection;
  tableClassName: string;
};

export const BuildingProfileTable = ({
  buildingFiles,
  isLoading,
  projectId,
  selectedTab,
  tableClassName,
}: BuildingProfileTableProps) => {
  const { t } = useTranslation();

  const filesTableColumns: TableColumn<BuildingProfileFile>[] = [
    {
      dataKey: "name",
      headerClassName: "sentence-case",
      label: t("BuildingProfile.table.columns.name"),
      valueTransformation: (row) => row["name"],
    },
    {
      dataKey: "type",
      headerClassName: "sentence-case",
      label: t("BuildingProfile.table.columns.type"),
      valueTransformation: (row) => row["type"],
    },
    {
      dataKey: "_updated_at",
      headerClassName: "sentence-case",
      label: t("BuildingProfile.table.columns.date modified"),
      valueTransformation: (row) => convertDatetimeStringToDate(row["_updated_at"]),
    },
    {
      cellType: "downloadFile",
      dataKey: "file_url",
      headerClassName: "sentence-case",
      href: (row) => row["file_url"],
      label: t("BuildingProfile.table.columns.files"),
      onCellClick: () => trackGAEvent(eventNames.BP_DOWNLOAD_SINGLE_FLOOR_PLANS),
      valueTransformation: (row) => (
        <IconButton
          buttonProps={{
            disabled: !row.file_url,
            size: "sm",
            variant: "tertiary",
          }}
          iconProps={{
            color: "var(--color-element-primary-initial)",
            iconName: "Download",
            size: "md",
          }}
        />
      ),
    },
  ];

  const { mutate } = useDeleteBuildingProfileFile();
  const handleDeleteFile = ({ rowId }: { rowId: string }) => mutate({ fileId: rowId, projectId } as any);

  return (
    <LoadingOrEmptyWrapper showLoading={isLoading}>
      <Table
        className={`mt-6 ${tableClassName}`}
        data={buildingFiles?.filter(
          (file) =>
            file.section === selectedTab.name ||
            (!file.section && selectedTab.name === buildingProfileFilesSections[0].name)
        )}
        headers={filesTableColumns}
        idColumnName="id"
        onDelete={handleDeleteFile}
        rowRemoveable={() => IsAllowed(resources.BUILDING_PROFILE.DELETE_FILE)}
      />
    </LoadingOrEmptyWrapper>
  );
};
